import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { registerPushWorker } from './register';
import { pushSupport } from './support';
import {
  PUSH_SUBSCRIPTIONS_QUERY_KEY,
  PUSH_THIS_DEVICE_QUERY_KEY,
  syncThisDevice,
  usePushConfig,
  usePushSubscriptions,
} from './useDevicePush';

/**
 * Story 30.3 — mounted once by the signed-in shell (`MainLayout`). Registers
 * the push worker (`/sw.js`, scope `/`) and, once the push config and my
 * device list have loaded, runs `syncThisDevice` ONCE: silent re-subscribe
 * after a VAPID key rotation, and `unsubscribe()` for a subscription my list
 * no longer holds. Renders nothing; does nothing at all in a browser that
 * cannot receive push (so no request is made there).
 */
export function PushDeviceSync() {
  const supported = pushSupport() === 'supported';
  return supported ? <PushDeviceSyncInner /> : null;
}

function PushDeviceSyncInner(): null {
  const queryClient = useQueryClient();
  const config = usePushConfig();
  const devices = usePushSubscriptions();
  const ran = useRef(false);

  useEffect(() => {
    void registerPushWorker();
  }, []);

  useEffect(() => {
    if (ran.current || !config.data || !devices.data) return;
    ran.current = true;
    void syncThisDevice({ config: config.data, devices: devices.data }).then((outcome) => {
      if (outcome === 'none') return;
      void queryClient.invalidateQueries({ queryKey: PUSH_THIS_DEVICE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PUSH_SUBSCRIPTIONS_QUERY_KEY });
    });
  }, [config.data, devices.data, queryClient]);

  return null;
}
